import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Link as LinkIcon } from 'lucide-react';
import type { RelationType } from '@arkitekt/shared';
import { Dialog, DialogHeader, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAuthStore } from '@/stores/auth.store';

const API_URL = import.meta.env.VITE_API_URL || '';
const API_BASE = `${API_URL}/api/v1`;

const RELATION_TYPES: RelationType[] = [
  'USES',
  'DEPENDS_ON',
  'OWNS',
  'BELONGS_TO',
  'INTEGRATES_WITH',
  'REALIZES',
];

const RELATION_LABELS: Record<RelationType, string> = {
  USES: 'Bruker',
  DEPENDS_ON: 'Avhenger av',
  OWNS: 'Eier',
  BELONGS_TO: 'Tilhører',
  INTEGRATES_WITH: 'Integrerer med',
  REALIZES: 'Realiserer',
};

export interface Relation {
  id: string;
  type: string;
  sourceId: string;
  targetId: string;
  source: { id: string; name: string; type: string };
  target: { id: string; name: string; type: string };
}

interface RelationManagerProps {
  objectId: string;
  objectName: string;
  spaceId: string;
  relations: Relation[];
  direction: 'outgoing' | 'incoming';
  onRelationsChange?: () => void;
}

export function RelationManager({
  objectId,
  objectName,
  spaceId,
  relations,
  direction,
  onRelationsChange,
}: RelationManagerProps) {
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((state) => state.accessToken);
  const [showAddModal, setShowAddModal] = useState(false);
  const [targetObjectId, setTargetObjectId] = useState('');
  const [relationType, setRelationType] = useState<RelationType>('USES');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_BASE}/relations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          sourceId: direction === 'outgoing' ? objectId : targetObjectId,
          targetId: direction === 'outgoing' ? targetObjectId : objectId,
          type: relationType,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to create relation');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objectRelations', spaceId, objectId] });
      setShowAddModal(false);
      setTargetObjectId('');
      setError(null);
      onRelationsChange?.();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create relation');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (relationId: string) => {
      const response = await fetch(`${API_BASE}/relations/${relationId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok && response.status !== 204) {
        throw new Error('Failed to delete relation');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objectRelations', spaceId, objectId] });
      onRelationsChange?.();
    },
  });

  const handleAddRelation = () => {
    if (!targetObjectId.trim()) {
      setError('Target object ID is required');
      return;
    }
    setError(null);
    createMutation.mutate();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-muted-foreground">
          {direction === 'outgoing' ? 'Utgående relasjoner' : 'Innkommende relasjoner'}
        </h4>
        <Button variant="ghost" size="sm" onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Legg til
        </Button>
      </div>

      {relations.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">Ingen {direction === 'outgoing' ? 'utgående' : 'innkommende'} relasjoner</p>
      ) : (
        <div className="space-y-2">
          {relations.map((rel) => {
            const linkedObject = direction === 'outgoing' ? rel.target : rel.source;
            return (
              <div
                key={rel.id}
                className="flex items-center justify-between p-2 bg-muted/50 rounded-lg group"
              >
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-muted-foreground" />
                  <Badge variant="outline" className="text-xs">
                    {RELATION_LABELS[rel.type as RelationType] || rel.type}
                  </Badge>
                  <span className="text-sm">{linkedObject.name}</span>
                  <Badge variant="secondary" className="text-xs">
                    {linkedObject.type}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                  onClick={() => deleteMutation.mutate(rel.id)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Relation Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal} className="max-w-md">
        <DialogHeader onClose={() => setShowAddModal(false)}>
          Legg til relasjon
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Fra objekt
            </label>
            <p className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
              {objectName} (nåværende)
            </p>
          </div>

          <div>
            <label htmlFor="relationType" className="block text-sm font-medium mb-1">
              Relasjonstype
            </label>
            <select
              id="relationType"
              value={relationType}
              onChange={(e) => setRelationType(e.target.value as RelationType)}
              className="w-full text-base md:text-sm px-3 py-2 border rounded-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            >
              {RELATION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {RELATION_LABELS[type]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="targetId" className="block text-sm font-medium mb-1">
              Mål-objekt ID
            </label>
            <input
              id="targetId"
              type="text"
              value={targetObjectId}
              onChange={(e) => setTargetObjectId(e.target.value)}
              placeholder="UUID til objektet du vil koble til..."
              className="w-full text-base md:text-sm px-3 py-2 border rounded-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Tips: Kopier objekt-ID fra URL-en på objektsiden
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowAddModal(false)}>
            Avbryt
          </Button>
          <Button onClick={handleAddRelation} disabled={createMutation.isPending || !targetObjectId.trim()}>
            {createMutation.isPending ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Oppretter...
              </>
            ) : (
              'Opprett relasjon'
            )}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
